import React, { useEffect, useState } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import { trans } from '../../../../_providers/Translation'
import '../../../../assets/css/partials/status-diagram.css'
import { PieChart } from '@mui/x-charts/PieChart'
import DateRange from '@/components/partials/DateRange'

export default function InvoicePaymentsStats({
    endpoint = 'dashboard/invoices-payments/stats',
    title = 'Фактури',
    colors = ['#5563FF', '#A855F7', '#C084FC', '#E879F9'],
    donut = true,
    size = 220
}) {
    const [state, setState] = useState({ loading: true, data: null })
    const [dateRange, setDateRange] = useState({
        from: new Date().toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    })

    const loadData = () => {
        setState(prev => ({ ...prev, loading: true }))
        const params = new URLSearchParams({
            date_from: dateRange.from,
            date_to: dateRange.to
        })

        Api.get(`${endpoint}?${params.toString()}`)
            .then(res => {
                const payload = res?.data ?? res
                setState({ loading: false, data: payload })
            })
            .catch(() => setState({ loading: false, data: null }))
    }

    useEffect(() => {
        loadData()
    }, [endpoint, dateRange])

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Convert API data format to items array
    const apiData = state.data || {}

    const dataSource = Object.keys(apiData).length > 0 ? apiData : []

    // Convert object to items array
    const itemsSource = [
        {
            key: 'paid',
            label: 'Платени',
            value: dataSource.paid?.count || 0,
            percentage: dataSource.paid?.percentage || 0,
            changePercent: dataSource.paid?.change_percent || null
        },
        {
            key: 'unpaid',
            label: 'Неплатени',
            value: dataSource.unpaid?.count || 0,
            percentage: dataSource.unpaid?.percentage || 0,
            changePercent: dataSource.unpaid?.change_percent || null
        },
        {
            key: 'sent',
            label: 'Изпратени',
            value: dataSource.sent?.count || 0,
            percentage: dataSource.sent?.percentage || 0,
            changePercent: dataSource.sent?.change_percent || null
        },
        {
            key: 'pending',
            label: 'Чакат плащане',
            value: dataSource.pending?.count || 0,
            percentage: dataSource.pending?.percentage || 0,
            changePercent: dataSource.pending?.change_percent || null
        }
    ]

    // Include all items for legend, but only render slices with values > 0
    const items = itemsSource
    const total = dataSource.total || items.reduce((s, i) => s + Number(i.value || 0), 0) || 1

    const pieData = items.map((it, idx) => ({
        id: idx,
        value: Number(it.value || 0),
        label: it.label,
        color: colors[idx % colors.length],
    }))

    function getTotalResults() {
        return items.reduce((sum, it) => sum + Number(it.value || 0), 0)
    }

    return (
        <div className="invoices-pie-chart">
            <div className='head'>
                <h3>
                    {title}
                </h3>
                <DateRange
                    startDate={dateRange.from}
                    endDate={dateRange.to}
                    onStartDateChange={(date) => {
                        handleDateChange('from', date)
                    }} onEndDateChange={(date) => {
                        handleDateChange('to', date)
                    }}
                />
            </div>
            <hr />
            <div className="body">
                {state.loading ?
                    <>
                        <div className='left-loading'>
                            <Skeleton height={200} count={1} />
                        </div>
                        <div className='right-loading'>
                            <Skeleton height={50} count={4} />
                        </div>
                    </>
                    :
                    getTotalResults() === 0 ?
                        <div
                            className="alert info"
                            style={{
                                marginBottom: 0,
                                width: '100%',
                            }}
                        >
                            {trans("messages.noDataFound")}
                        </div>
                        :
                        <>
                            <div className='left'>
                                <PieChart
                                    series={[{
                                        data: pieData,
                                        valueFormatter: (item) => {
                                            const pct = total ? ((item.value / total) * 100).toFixed(2) : 0
                                            return `${item.value} (${pct}%)`
                                        },
                                    }]}
                                    width={200}
                                    height={200}
                                    margin={{ right: 0 }}
                                    slotProps={{
                                        legend: { hidden: true },
                                    }}
                                />
                            </div>

                            <div className='right'>
                                <table>
                                    <tbody>

                                        {items.map((it, idx) => {
                                            const percentText = `${it.percentage?.toFixed(2) || 0}%`
                                            const changePercent = it.changePercent
                                            const changeClass = changePercent && changePercent.toString().startsWith('-') ? 'negative' : 'positive'
                                            const color = colors[idx % colors.length]
                                            return (
                                                <tr key={it.key || it.label || idx}>
                                                    <td>
                                                        <span style={{ background: color }} />
                                                    </td>
                                                    <td>
                                                        <b>
                                                            {it.label || it.name}
                                                        </b>
                                                    </td>
                                                    <td>
                                                        <div className='count'>
                                                            {percentText}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={`percentage ${changeClass}`}>
                                                            {`(${changePercent ? percentText : "0"}%)`}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                }
            </div>
        </div>
    )
}